import { BASE_URL } from "@src/utils";
import axios from "../axios";
import QueryString from "qs";
import { TResPartnerInfo, TResPartnerConsultingCompany } from "@src/types/partner";

export interface IPartner {
  partnerId: number;
  partnerType: number;
  coverUrl: string;
  informReceiver: string;
  uuid: string;
  bizName: string;
  content: string | null;
  specialFeatures: {
    icon: string;
    features: string[];
  }[];
  infoComponents: IPartnerInfoComponent[] | null;
  notice: string | null;
  partnerVisitGuides: IPartnerVisitGuide[] | null;
  seeMoreComponents: IPartnerInfoComponent[] | null;
  button: {
    type: string;
    name: string;
    url: string;
  };
  benefits: IPartnerBenefit[];
  like: number | null;
  shareLink: string;
  isInquire: number;
  logoUrl: string | null;
  partnerTypeName: string;
  reviewSummary: {
    branchPartnerCnt: number;
    reviewCnt: number;
    totalReviewCnt: number;
  };
  partnerTabs: {
    name: string;
    code: string;
    viewState: number;
  }[];
  thumbnailTag?: string; // 추가
}
// 최소 파트너 정보
export interface IPartnerMinimum {
  uuid: string; //필수
  bizName?: string;
  coverUrl?: string;
  logoUrl?: string;
  partnerTypeName?: string;
  reviewSummary?: {
    branchPartnerCnt: number;
    reviewCnt: number;
    totalReviewCnt: number;
  };
  [key: string]: any;
}
export interface IPartnerVisitGuide {
  partnerName: string;
  address: string;
  address2: string | null;
  operationTime: IPartnerVisitGuideOperationTime[] | null;
  bizHour: string;
  parking: string;
  bizTel: string;
  homepageUrl: string | null;
}
export interface IPartnerVisitGuideOperationTime {
  key: string;
  isHoliday: boolean;
  startTime: string;
  endTime: string;
}
export interface IPartnerBenefit {
  title: string;
  content: string;
  iconUrl: string;
  tags?: string[];
  tag?: string;
  //웨딩홀 이벤트 배너 이미지 추가
  bannerUrl?: string;
  bannerLink?: string;
}

export interface IPartnerImages {
  albumId: any;
  content: string | null;
  title: string | null;
  url: string;
}
export interface IPartnerBranchItem {
  link: string;
  name: string;
}
export const getPartnerImages = (
  uuid: string,
  params: { fetchSize: number; fetchStart: number },
) => {
  return axios.get<{ item: IPartnerImages[]; total: number }>(
    `${BASE_URL}/web/v3/230531/partner/${uuid}/image?category=0&fetchStart=${params.fetchStart}&fetchSize=${params.fetchSize}`,
  );
};
export const getPartner = (uuid: string) => {
  return axios.get<{ item: IPartner }>(`${BASE_URL}/v3/20240729/partner/${uuid}/detail`);
};
export const getPartnerPreview = (uuid: string, partnerProfileUuid: string) => {
  return axios.get<{ item: IPartner }>(
    `${BASE_URL}/v3/20240729/partner/${uuid}/detail?partnerProfileUuid=${partnerProfileUuid}`,
  );
};
export const getPartnerRegions = (partnerType: number) => {
  return axios.get<{ item: { id: number; name: string }[] }>(
    `${BASE_URL}/v4/region-groups?partnerType=${partnerType}`,
  );
};
export const getPartnerList = (
  regionGroupIds: string,
  partnerType: number,
  fetchStart: number,
  fetchSize: number,
) => {
  return axios.get<{ item: TResPartnerInfo[]; total: number }>(
    `${BASE_URL}/v4/partners?partnerType=${partnerType}&fetchStart=${fetchStart}&fetchSize=${fetchSize}${regionGroupIds !== null ? `&regionGroupIds=${regionGroupIds}` : ""}`,
  );
};
// 업체 조회 (쉼표로 uuid 연결해서 요청)
export const getPartners = ({ partnerUuids }: { partnerUuids: string }) => {
  return axios.get<{ item: IPartner[] }>(`${BASE_URL}/v4/partners?partnerUuids=${partnerUuids}`);
};

// TODO 새로생긴 찜 api 있음 - 어떻게 사용할지 확인 필요
export const updatePartnerLike = (uuid: string, targetIsLikeState: boolean) => {
  // targetIsLikeState - 목표로 하는 좋아요 상태
  // 목표 좋아요 상태가 true이면 좋아요 1, false면 좋아요 취소 0
  const state = targetIsLikeState ? 1 : 0;

  return axios.put(`${BASE_URL}/v3/20240729/partner/${uuid}/like`, { state: state });
};

export const getPartnerType = (uuid: string) => {
  return axios.get<{ item: number }>(`${BASE_URL}/v3/partner/${uuid}/type`);
};

export const getPartnerBranch = (uuid: string) => {
  return axios.get<{ item: IPartnerBranchItem[]; total: number }>(
    `${BASE_URL}/web/v3/20240909/partner/${uuid}/booking-available-partners`,
  );
};

export const getPartnerConsultingCompany = async (uuid: string) => {
  const res = await axios.get<{ item: TResPartnerConsultingCompany }>(
    `${BASE_URL}/v4/partners/${uuid}/consulting`,
  );

  return res.data.item;
};

// 문의 하기
export interface IInquiry {
  content: string;
  customerNick: string;
  setCustomerUuid: string;
  depth: number;
  regTsp: string;
  replyCnt: number;
  uuid: string;
}
export const getPartnerInquiry = (uuid: string) => {
  return axios.get<{ item: IInquiry[] }>(
    `${BASE_URL}/web/v3/230531/partner/${uuid}/qna?fetchSize=200`,
  );
};
export const postPartnerInquiry = (uuid: string, content: string) => {
  // QueryString.stringify({partnerUuid: partner.uuid}
  return axios.post(
    `${BASE_URL}/web/v3/230531/partner/${uuid}/qna`,
    QueryString.stringify({ content: content }),
  );
};

// 리뷰 작성
export const postPartnerReview = (data: any) => {
  return axios.post<{ item: IPartner[] }>(
    `${BASE_URL}/web/v1/communityV2`,
    QueryString.stringify(data),
  );
};
export const getPartnerReview = (data: any) => {
  return axios.get<{ item: IPartner[] }>(
    `${BASE_URL}/web/v1/communityV2?${QueryString.stringify(data)}`,
  );
};

// ========== 혼수 방문예약 API ==========

/** 업체 슬롯 조회 (예약 슬롯 사용 업체용) */
export interface IPartnerSlotResponse {
  item?: {
    displayed?: boolean;
    slotInterval?: number;
    daySettings?: { dayOfWeek: string; startTime: string; endTime: string }[];
    slots?: {
      slotDate?: string;
      slotTime?: string;
      dayOfWeek?: string;
      status?: string;
      deposit?: number;
      reserved?: boolean;
      isReserved?: boolean;
      closed?: boolean;
      disabled?: boolean;
    }[];
  };
}

export const getHonsooPartnerSlots = (uuid: string) => {
  return axios.get<IPartnerSlotResponse>(`${BASE_URL}/v4/20240927/honsoo/reservation/partner/${uuid}`);
};

/** 혼수 예약 신청 */
export interface IPartnerBooking {
  uuid?: string;
  partnerUuid: string;
  name: string;
  tel: string;
  personnel: string;
  visitTsp: string;
  memo?: string;
}

export const postPartnerBooking = (params: IPartnerBooking) => {
  const requestBody = QueryString.stringify(params);
  return axios.post<{ item?: { uuid?: string } }>(`${BASE_URL}/v3/200618/partner/booking`, requestBody, {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  });
};

/** 혼수 결제 준비 */
export interface IHonsooPaymentReadyRequest {
  uuid?: string;
  partnerUuid: string;
  visitTsp: string;
  name: string;
}

export const postHonsooPaymentReady = (params: IHonsooPaymentReadyRequest) => {
  return axios.post<{ item: { orderId: string } }>(
    `${BASE_URL}/v4/20240927/honsoo/reservation/payment/ready`,
    params,
  );
};

/** 혼수 결제 확정 */
export interface IHonsooPaymentConfirmRequest {
  orderId: string;
  paymentKey: string;
  paymentAmount: number;
  partnerUuid?: string;
  visitTsp?: string;
  name?: string;
  tel?: string;
  personnel?: string;
}

export const postHonsooPaymentConfirm = (params: IHonsooPaymentConfirmRequest) => {
  return axios.post<{ item: { firstInformId: number } }>(
    `${BASE_URL}/v4/20240927/honsoo/reservation/payment/confirm`,
    params,
  );
};

// 파트너 상세 정보 컴포넌트 종류별 타입
type IPartnerInfoComponent_Base = {
  type: string;
  lastUpdateTime?: number; // 웹뷰에선 사용할 일 없음
  rowId?: number; // 웹뷰에선 사용할 일 없음
};
export type IPartnerInfoComponent_Image = IPartnerInfoComponent_Base & {
  imageUrl: string;
  imageName: string;
  appLink: string;
  webLink: string;
  imageSpace: "1" | "2";
};
export type IPartnerInfoComponent_BigTitle = IPartnerInfoComponent_Base & {
  title: string;
};
export type IPartnerInfoComponent_BigText = IPartnerInfoComponent_Base & {
  text: string;
};
export type IPartnerInfoComponent_Benefit = IPartnerInfoComponent_Base & {
  title: string;
  benefits: {
    emoji: string;
    content: string;
  }[];
};
export type IPartnerInfoComponent_TextBox = IPartnerInfoComponent_Base & {
  title: string;
  contentType: "text" | "bullet";
  content?: string;
  bullets?: string[];
};
export type IPartnerInfoComponent_PopularProduct = IPartnerInfoComponent_Base & {
  imageUrl: string;
  imageName: string;
  productName: string;
  productContent: string;
  salePrice: string | number;
  beforeDiscountPrice: string | number;
  benefit1: string;
  benefit2: string;
  benefit3: string;
  link: string;
};
export type IPartnerInfoComponent_CombinedPopularProduct = IPartnerInfoComponent_Base & {
  products: IPartnerInfoComponent_PopularProduct[];
};
export type IPartnerInfoComponent_Qna = IPartnerInfoComponent_Base & {
  qnaList: {
    question: string;
    answer: string;
  }[];
};
export type IPartnerInfoComponent_Table = IPartnerInfoComponent_Base & {
  title: string;
  guide: string;
  labelPrice: string;
  labelNote: string;
  tableContents: {
    name: string;
    price: string;
    note: string;
  }[];
};
export type IPartnerInfoComponent_Video = IPartnerInfoComponent_Base & {
  videoUrl: string;
};
export type IPartnerInfoComponent_Note = IPartnerInfoComponent_Base & {
  title: string;
  content: string;
};
export type IPartnerInfoComponent_SmallTitle = IPartnerInfoComponent_Base & {
  title: string;
};
export type IPartnerInfoComponent_SmallText = IPartnerInfoComponent_Base & {
  text: string;
};
export type IPartnerInfoComponent_SwipeImage = IPartnerInfoComponent_Base & {
  title: string;
  content: string;
  imageList: {
    imageUrl: string;
    imageName: string;
  }[];
  autoRolling: "ON" | "OFF";
};
export type IPartnerInfoComponent_Button1 = IPartnerInfoComponent_Base & {
  text: string;
  link: string;
};
export type IPartnerInfoComponent_Button2 = IPartnerInfoComponent_Base & {
  text: string;
  link: string;
};
export type IPartnerInfoComponent_Tags = IPartnerInfoComponent_Base & {
  tags: string[];
};
export type IPartnerInfoComponent_ProductCard = IPartnerInfoComponent_Base & {
  imageUrl: string;
  imageName: string;
  productName: string;
  contents: {
    label: string;
    content: string;
  }[];
};
export type IPartnerInfoComponent_Divider = IPartnerInfoComponent_Base & {};
export type IPartnerInfoComponent_BulletText = IPartnerInfoComponent_Base & {
  title: string;
  bulletTexts: string[];
};
export type IPartnerInfoComponent_LocationInfo = IPartnerInfoComponent_Base & {};
export type IPartnerInfoComponent_CustomLocationInfo = IPartnerInfoComponent_Base & {
  content: string | null;
  partnerVisitGuides: IPartnerVisitGuide[] | null;
};
export type IPartnerInfoComponent_ConsultingCompany = IPartnerInfoComponent_Base & {};
export type IPartnerInfoComponent_CustomConsultingCompany = IPartnerInfoComponent_Base & {
  partnerUuid: string;
};
export type IPartnerInfoComponent_Magazine = IPartnerInfoComponent_Base & {
  magazines: {
    magazineLink: string;
    title: string;
    subTitle: string;
    appLink: string;
    coverUrl?: string;
  }[];
};
export type IPartnerInfoComponent_Recommendation = IPartnerInfoComponent_Base & {
  recommendationPartners: {
    partnerTypeName: string;
    partnerName: string;
    coverUrl?: string;
    appLink: string;
    webLink: string;
  }[];
};
export type IPartnerInfoComponent_Related = IPartnerInfoComponent_Base & {
  relatedPartners: {
    partnerTypeName: string;
    partnerName: string;
    coverUrl?: string;
    appLink: string;
    webLink: string;
  }[];
};

export type IPartnerInfoComponent =
  | IPartnerInfoComponent_Image
  | IPartnerInfoComponent_BigTitle
  | IPartnerInfoComponent_BigText
  | IPartnerInfoComponent_Benefit
  | IPartnerInfoComponent_PopularProduct
  | IPartnerInfoComponent_CombinedPopularProduct
  | IPartnerInfoComponent_Qna
  | IPartnerInfoComponent_TextBox
  | IPartnerInfoComponent_Table
  | IPartnerInfoComponent_Note
  | IPartnerInfoComponent_Video
  | IPartnerInfoComponent_SmallTitle
  | IPartnerInfoComponent_SmallText
  | IPartnerInfoComponent_SwipeImage
  | IPartnerInfoComponent_Button1
  | IPartnerInfoComponent_Button2
  | IPartnerInfoComponent_Tags
  | IPartnerInfoComponent_ProductCard
  | IPartnerInfoComponent_Divider
  | IPartnerInfoComponent_BulletText
  | IPartnerInfoComponent_LocationInfo
  | IPartnerInfoComponent_ConsultingCompany
  | IPartnerInfoComponent_Magazine
  | IPartnerInfoComponent_Recommendation
  | IPartnerInfoComponent_Related;
